import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Params,
  Router,
  UrlTree,
} from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import {
  ANALYZING_MODES,
  MODULES,
  REFERENCE_PERMISSION_ACTIONS,
  USER_TYPES,
} from '@shared/constants';
import { generatePermissionKey } from '@shared/utils';

import { LoggedUserService } from '../../auth/services';
import { ReferenceCategoryService } from '../../references/services';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {
  constructor(
    private router: Router,
    private location: Location,
    private loggedUserService: LoggedUserService,
    private referenceCategoryService: ReferenceCategoryService
  ) {}

  private checkPermission(
    _allowedPermissions?: string[],
    _analyzingMode = ANALYZING_MODES.EVERY,
    module?: MODULES,
    params?: Params
  ): Promise<boolean | UrlTree> {
    let allowedPermissions: string[] = [...(_allowedPermissions ?? [])];
    let analyzingMode = _analyzingMode;
    const clearAuthListening = new Subject<void>();

    return new Promise((resolve) => {
      const resolveAccess = (accessGranted: boolean) => {
        if (!accessGranted) {
          const destination = this.router
            .getCurrentNavigation()
            ?.extractedUrl?.toString();
          this.router
            .navigateByUrl('/app/access-denied', { skipLocationChange: true })
            .then(() => {
              this.location.replaceState(destination);
            });
        }

        resolve(accessGranted);
      };

      this.loggedUserService.dataStore
        .pipe(takeUntil(clearAuthListening))
        .subscribe(async (data) => {
          if (data) {
            clearAuthListening.next();

            // Allow everything to EL Admin
            if (data?.user_type === USER_TYPES.EL_ADMIN) {
              return resolveAccess(true);
            }

            const saasPermission: string[] =
              data.tenant.subscription_data?.permissions || [];

            let assignedPermission: string[] =
              data.connection.permissions || [];

            if (saasPermission?.length) {
              assignedPermission = assignedPermission.filter(
                (singlePermission) => saasPermission.includes(singlePermission)
              );
            }

            if (module === MODULES.REFERENCES) {
              analyzingMode = ANALYZING_MODES.SOME;

              const category =
                await this.referenceCategoryService.getCategoryById(
                  params.categoryId
                );

              if (!category) {
                return resolveAccess(false);
              } else if (category.is_public) {
                return resolveAccess(true);
              }

              const permission = allowedPermissions.map((permission) =>
                generatePermissionKey(
                  MODULES.REFERENCES,
                  permission as REFERENCE_PERMISSION_ACTIONS
                )
              );

              allowedPermissions = [
                ...permission,
                ...allowedPermissions.map((permission) =>
                  generatePermissionKey(
                    MODULES.REFERENCES,
                    permission as REFERENCE_PERMISSION_ACTIONS,
                    category.name
                  )
                ),
              ];
            }

            // allow if allowed permissions are not defined
            if (!allowedPermissions || allowedPermissions.length === 0) {
              return resolveAccess(true);
            }

            if (
              analyzingMode === ANALYZING_MODES.EVERY &&
              allowedPermissions.every((permission) =>
                assignedPermission.includes(permission)
              )
            ) {
              return resolveAccess(true);
            } else if (
              analyzingMode === ANALYZING_MODES.SOME &&
              allowedPermissions.some((permission) =>
                assignedPermission.includes(permission)
              )
            ) {
              return resolveAccess(true);
            } else {
              console.log(
                `[DEBUG PermissionGuard]: Navigated to the Access Denied page because of the permission mismatch`
              );

              console.log('Required Permissions:', allowedPermissions);
              console.log('Current Permissions:', assignedPermission);

              return resolveAccess(false);
            }
          } else {
            // Don't return anything.
            // Since the loggedUserService.dataStore is internally observing until a data return,
            // subscription will return a value in future.
            // The guard resolver will wait until a value returns.
            // ------------------------------------------------------------------------------- //
            // ------------------------------------------------------------------------------- //
            // If we return resolveAccess(false); here, guard resolver will thing
            // "Okay, this user have no access, and it is the final output".
            // It will lead to see the "Access Denied" page on the screen
          }
        });
    });
  }

  canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    return this.checkPermission(
      route.data?.allowedPermissions,
      route.data?.analyzingMode ?? undefined,
      route.data?.module,
      route.params
    );
  }
}
